import { X } from 'lucide-react-native';
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import { Box, FormInput, HStack, IconButton, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  MAX_PRODUCT_ALIAS_LENGTH,
  MAX_PRODUCT_ALIASES,
  MAX_PRODUCT_ALIASES_INPUT_LENGTH,
  mergeProductAliases,
  type RejectedAlias,
  type RejectedAliasReason,
} from '../utils/productNaming';

const REJECTION_ORDER: RejectedAliasReason[] = ['too_long', 'capped', 'duplicate'];

const REJECTION_KEYS: Record<RejectedAliasReason, string> = {
  too_long: 'onboardingWizard.products.aliases.rejected.tooLong',
  capped: 'onboardingWizard.products.aliases.rejected.capped',
  duplicate: 'onboardingWizard.products.aliases.rejected.duplicate',
};

interface ProductAliasesInputProps {
  testIDPrefix: string;
  aliases: string[];
  productName: string;
  onChange: (aliases: string[]) => void;
}

export function ProductAliasesInput({
  testIDPrefix,
  aliases,
  productName,
  onChange,
}: Readonly<ProductAliasesInputProps>) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [rejections, setRejections] = useState<RejectedAlias[]>([]);

  const rejectedReasons = REJECTION_ORDER.filter((reason) =>
    rejections.some((entry) => entry.reason === reason),
  );
  const atLimit = aliases.length >= MAX_PRODUCT_ALIASES;
  const canAdd = text.trim().length > 0 && !atLimit;

  const commit = () => {
    if (!canAdd) return;
    const { aliases: next, rejected } = mergeProductAliases(aliases, text.split(','), productName);
    if (next.length !== aliases.length) onChange(next);
    setText(rejected.map((entry) => entry.alias).join(', '));
    setRejections(rejected);
  };

  const handleTextChange = (value: string) => {
    setText(value);
    setRejections([]);
  };

  const removeAt = (index: number) => {
    onChange(aliases.filter((_, position) => position !== index));
  };

  return (
    <VStack spacing="xs" testID={testIDPrefix}>
      <Text variant="caption" color={colors.text.secondary} style={styles.label}>
        {t('onboardingWizard.products.aliases.label')}
      </Text>
      <HStack spacing="sm" align="center">
        <Box style={styles.field}>
          <FormInput
            testID={`${testIDPrefix}-input`}
            value={text}
            onChangeText={handleTextChange}
            onSubmitEditing={commit}
            placeholder={t('onboardingWizard.products.aliases.placeholder')}
            accessibilityLabel={t('onboardingWizard.products.aliases.label')}
            maxLength={MAX_PRODUCT_ALIASES_INPUT_LENGTH}
            editable={!atLimit || text.length > 0}
            returnKeyType="done"
            style={styles.input}
          />
        </Box>
        <Button
          testID={`${testIDPrefix}-add`}
          variant="outline"
          size="sm"
          onPress={commit}
          disabled={!canAdd}
          accessibilityState={{ disabled: !canAdd }}
          accessibilityLabel={t('onboardingWizard.products.aliases.add')}
        >
          {t('onboardingWizard.products.aliases.add')}
        </Button>
      </HStack>
      <Text variant="caption" color={colors.text.secondary}>
        {t('onboardingWizard.products.aliases.helper')}
      </Text>
      {rejectedReasons.length > 0 ? (
        <VStack spacing="xxs" testID={`${testIDPrefix}-rejected`} accessibilityLiveRegion="polite">
          {rejectedReasons.map((reason) => (
            <VStack key={reason} spacing="xxs">
              <Text
                testID={`${testIDPrefix}-rejected-${reason}`}
                variant="caption"
                color={colors.status.warning.main}
              >
                {t(REJECTION_KEYS[reason], {
                  max: reason === 'too_long' ? MAX_PRODUCT_ALIAS_LENGTH : MAX_PRODUCT_ALIASES,
                })}
              </Text>
              <Text
                testID={`${testIDPrefix}-rejected-${reason}-aliases`}
                variant="caption"
                color={colors.text.secondary}
              >
                {rejections
                  .filter((entry) => entry.reason === reason)
                  .map((entry) => entry.alias)
                  .join(', ')}
              </Text>
            </VStack>
          ))}
        </VStack>
      ) : null}
      {atLimit ? (
        <Text
          testID={`${testIDPrefix}-limit`}
          variant="caption"
          color={colors.status.warning.main}
          accessibilityRole="alert"
        >
          {t('onboardingWizard.products.aliases.limit', { max: MAX_PRODUCT_ALIASES })}
        </Text>
      ) : null}
      {aliases.length > 0 ? (
        <Box style={styles.chips}>
          {aliases.map((alias, index) => (
            <HStack
              key={`${index}-${alias}`}
              spacing="xs"
              align="center"
              testID={`${testIDPrefix}-chip-${index}`}
              style={styles.chip}
            >
              <Text variant="caption" color={colors.text.primary}>
                {alias}
              </Text>
              <IconButton
                testID={`${testIDPrefix}-remove-${index}`}
                icon={<X size={14} color={colors.text.secondary} />}
                variant="ghost"
                size="lg"
                onPress={() => removeAt(index)}
                accessibilityLabel={t('onboardingWizard.products.aliases.removeLabel', { alias })}
              />
            </HStack>
          ))}
        </Box>
      ) : null}
    </VStack>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '600',
  },
  field: {
    flex: 1,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.xs,
  },
  chip: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    paddingLeft: tokens.spacing.sm,
  },
});
